'use client';

import { useTranslations } from 'next-intl';
import { Image } from 'react-datocms';
import { Card } from '~/components/Card';
import { Heading } from '~/components/Heading';
import { Stack } from '~/components/Layout/Stack';
import { Link } from '~/components/Link';
import { api } from '~/libs/api';
import { getDestinationSlug } from '~/utils/datoHelpers';
import { Section } from '../../Section';
import styles from './Equaldex.module.css';
import type { EqualdexProps } from './Equaldex.types';

export const Equaldex = ({ data, countryId }: EqualdexProps) => {
  const t = useTranslations();
  const { isLoading, data: equaldexRegionData, error } = api.equaldex.getRegion.useQuery(countryId);

  const { title, ctaLabel, image, description } = data;

  if (isLoading) return <div>{t('common.loading')}</div>;
  if (error) return <div>{t('content.feedback.error_with_message', { message: error.message })}</div>;
  if (!equaldexRegionData) return <div>{t('content.feedback.data_empty')}</div>;

  const { name, ei, rank } = equaldexRegionData;

  // override for Czechia - naming not consistent in equaldex's api, Czechia => /czech-republic in their url
  const checkedName = name.toLowerCase() === 'czechia' ? 'Czech Republic' : name;

  return (
    <Section>
      <Card isElevated rounded="--by-border-radius-300">
        <div className={styles.cardContent}>
          <div className={styles.imageSection}>
            {image?.responsiveImage ? (
              <Image data={image.responsiveImage} />
            ) : (
              <div className={styles.imagePlaceholder}>{t('content.image_unavailable')}</div>
            )}
          </div>
          <div className={styles.contentSection}>
            <Stack space="--by-space-400">
              <Heading level="h3" variant="heading-xs">
                {title}
              </Heading>
              <p>{t('content.equaldex_ranking', { name, rank, ei })}</p>
              {description && <p>{description}</p>}
              {ctaLabel && (
                <Link
                  asButton="primary"
                  href={`https://equaldex.com/region/${getDestinationSlug(checkedName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  isExternal
                  data-ua-click="equaldex_cta"
                >
                  {ctaLabel}
                </Link>
              )}
            </Stack>
          </div>
        </div>
      </Card>
    </Section>
  );
};

export default Equaldex;
